import React, { useEffect, useState } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Coins, Plus, Loader2, Download, Grid3X3, ArrowRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Header, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';
import { route } from '../../../shared/utils';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

const GOLD = '#FFD93D';

const PLOT_ICONS: Record<string, React.ReactNode> = {
  plot_regular: (
    <svg viewBox="0 0 40 40" width="36" height="36">
      <rect x="4" y="6" width="32" height="28" rx="1.5" fill="rgba(255,217,61,0.06)" stroke={GOLD} strokeWidth="2"/>
      <line x1="4" y1="20" x2="36" y2="20" stroke={GOLD} strokeWidth="0.4" opacity="0.25" strokeDasharray="2,2"/>
      <line x1="20" y1="6" x2="20" y2="34" stroke={GOLD} strokeWidth="0.4" opacity="0.25" strokeDasharray="2,2"/>
    </svg>
  ),
  plot_regular_room: (
    <svg viewBox="0 0 40 40" width="36" height="36">
      <rect x="4" y="6" width="32" height="28" rx="1.5" fill="rgba(255,217,61,0.06)" stroke={GOLD} strokeWidth="2"/>
      {/* Rooms inside */}
      <rect x="7" y="9" width="12" height="10" rx="1" fill="rgba(255,217,61,0.18)" stroke={GOLD} strokeWidth="1.2"/>
      <rect x="22" y="9" width="11" height="10" rx="1" fill="rgba(161,161,170,0.1)" stroke="#666" strokeWidth="0.8"/>
      <rect x="7" y="22" width="12" height="9" rx="1" fill="rgba(161,161,170,0.1)" stroke="#666" strokeWidth="0.8"/>
      <rect x="22" y="22" width="11" height="9" rx="1" fill="rgba(161,161,170,0.1)" stroke="#666" strokeWidth="0.8"/>
    </svg>
  ),
  plot_irregular: (
    <svg viewBox="0 0 40 40" width="36" height="36">
      <polygon points="8,8 34,5 36,34 5,32" fill="rgba(255,217,61,0.06)" stroke={GOLD} strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="8" cy="8" r="2" fill={GOLD}/>
      <circle cx="34" cy="5" r="2" fill={GOLD}/>
      <circle cx="36" cy="34" r="2" fill={GOLD}/>
      <circle cx="5" cy="32" r="2" fill={GOLD}/>
    </svg>
  ),
  plot_irregular_room: (
    <svg viewBox="0 0 40 40" width="36" height="36">
      <polygon points="8,8 34,5 36,34 5,32" fill="rgba(255,217,61,0.06)" stroke={GOLD} strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="8" cy="8" r="2" fill={GOLD}/>
      <circle cx="34" cy="5" r="2" fill={GOLD}/>
      <circle cx="36" cy="34" r="2" fill={GOLD}/>
      <circle cx="5" cy="32" r="2" fill={GOLD}/>
      {/* Room inside */}
      <rect x="13" y="12" width="11" height="9" rx="1" fill="rgba(255,217,61,0.18)" stroke={GOLD} strokeWidth="1.2"/>
      <rect x="13" y="24" width="11" height="8" rx="1" fill="rgba(161,161,170,0.1)" stroke="#666" strokeWidth="0.8"/>
    </svg>
  ),
};

async function apiFetch(endpoint: string, token?: string) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
  };
  if (token) headers[config.auth.headers.auth] = `Bearer ${token}`;
  const res = await fetch(`${config.api.baseUrl}${endpoint}`, { headers });
  if (!res.ok) return null;
  return res.json();
}

interface Service {
  code: string;
  name: string;
  description: string;
  tier1Price: number;
  tier2Price: number;
}

const Dashboard: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [balance, setBalance] = useState<number | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [recentDesigns, setRecentDesigns] = useState<any[]>([]);
  const [recentDownloads, setRecentDownloads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const fetchData = async () => {
      try {
        const [balRes, svcRes, designRes, downloadRes] = await Promise.allSettled([
          apiFetch('/api/credit/balance', token),
          apiFetch('/api/service/list'),
          apiFetch('/api/design/list?page=1&pageSize=5', token),
          apiFetch('/api/download/history?page=1&pageSize=5', token),
        ]);
        if (balRes.status === 'fulfilled' && balRes.value?.data) setBalance(balRes.value.data.balance ?? 0);
        if (svcRes.status === 'fulfilled' && svcRes.value?.data) setServices(Array.isArray(svcRes.value.data) ? svcRes.value.data : []);
        if (designRes.status === 'fulfilled' && designRes.value?.data?.designs) setRecentDesigns(designRes.value.data.designs);
        if (downloadRes.status === 'fulfilled' && downloadRes.value?.data?.downloads) setRecentDownloads(downloadRes.value.data.downloads);
      } catch { /* silently handle */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [token]);

  if (!user) return null;

  const firstName = user.name?.split(' ')[0] || 'there';

  return (
    <PageLayout size="full">
      <SEO title="Dashboard - RRD Planners" description="Your RRD Planners dashboard" />
      <Header />
      <PageLayout.Content>
        <div style={{ padding: isMobile ? '16px 12px 32px' : '28px 20px 48px' }}>

          {/* Top bar: greeting + balance */}
          <div style={{
            display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 20 : 32,
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <div>
              <h1 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 700, color: '#fff', margin: 0 }}>
                Hello, {firstName} 👋
              </h1>
              <p style={{ color: '#666', fontSize: isMobile ? 13 : 14, marginTop: 4 }}>
                What would you like to design today?
              </p>
            </div>

            {/* Balance pill */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
              padding: isMobile ? '10px 12px' : '12px 16px',
              width: isMobile ? '100%' : 'auto',
            }}>
              <div style={{
                width: 36, height: 36, background: 'rgba(255,217,61,0.12)',
                borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Coins size={18} style={{ color: GOLD }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Balance</div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: GOLD, lineHeight: 1.1 }}>
                  {loading ? '...' : `₹${balance ?? 0}`}
                </div>
              </div>
              <Link to={route('/credits/buy')} style={{ marginLeft: 'auto' }}>
                <button style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px',
                  background: GOLD, color: '#1a1a1a', border: 'none', borderRadius: 7,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  <Plus size={14} /> Top Up
                </button>
              </Link>
            </div>
          </div>

          {/* Main layout: Plot types left, Activity right */}
          <div style={{ display: 'flex', gap: 20, alignItems: isMobile ? 'stretch' : 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>

          {/* Left: Plot Types */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: GOLD, textTransform: 'uppercase', letterSpacing: 1 }}>
              Choose Plot Type
            </span>
            <div style={{ flex: 1, height: 1, background: '#2a2a2a' }} />
          </div>

          {/* Service List */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 0' }}>
              <Loader2 size={28} className="animate-spin" style={{ color: GOLD }} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: isMobile ? 20 : 36 }}>
              {services.map((svc) => (
                <div
                  key={svc.code}
                  style={{
                    background: '#141414', border: '1px solid #222', borderRadius: 12,
                    padding: isMobile ? '14px' : '16px 18px',
                    display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 12 : 16,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,217,61,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#222'; }}
                >
                  {/* Top row: Icon + Info + Pricing */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16, flex: isMobile ? undefined : 1, minWidth: 0 }}>
                    {/* Icon */}
                    <div style={{
                      width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, flexShrink: 0,
                      background: 'rgba(255,217,61,0.06)', border: '1px solid rgba(255,217,61,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {PLOT_ICONS[svc.code] || PLOT_ICONS.plot_regular}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ color: '#e5e5e5', fontSize: isMobile ? 13 : 14, fontWeight: 600, margin: 0 }}>{svc.name}</h3>
                      {!isMobile && <p style={{ color: '#555', fontSize: 12, margin: '2px 0 0' }}>{svc.description}</p>}
                    </div>

                    {/* Pricing */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 9, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>New</div>
                        <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: GOLD }}>₹{svc.tier1Price}</span>
                      </div>
                      <div style={{ width: 1, height: 22, background: '#2a2a2a' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 9, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>Edit</div>
                        <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: '#666' }}>₹{svc.tier2Price}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => navigate(route(`/services/generator?service=${svc.code}&content=empty`))}
                      style={{
                        flex: isMobile ? 1 : undefined,
                        padding: '7px 16px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                        background: GOLD, color: '#1a1a1a', border: 'none',
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Start New
                    </button>
                    <button
                      onClick={() => navigate(route(`/services/generator?service=${svc.code}&content=sample`))}
                      style={{
                        flex: isMobile ? 1 : undefined,
                        padding: '7px 16px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                        background: 'transparent', color: '#888', border: '1px solid #333',
                        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#ccc'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = '#888'; }}
                    >
                      Sample
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          </div>{/* End left column */}

          {/* Right: Recent Activity */}
          <div style={{ flex: isMobile ? undefined : '0 0 340px', minWidth: 0, width: isMobile ? '100%' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: GOLD, textTransform: 'uppercase', letterSpacing: 1 }}>
              Recent Activity
            </span>
            <div style={{ flex: 1, height: 1, background: '#2a2a2a' }} />
            <button
              onClick={() => navigate(route('/activity'))}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
                color: '#666', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = GOLD; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#666'; }}
            >
              View All <ArrowRight size={13} />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Recent Designs */}
            <div style={{ background: '#141414', border: '1px solid #272727', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', borderBottom: '1px solid #272727',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ccc', fontSize: 13, fontWeight: 600 }}>
                  <Grid3X3 size={15} style={{ color: GOLD }} /> My Designs
                </div>
              </div>
              {recentDesigns.length === 0 ? (
                <div style={{ padding: '28px 16px', textAlign: 'center', color: '#555', fontSize: 13 }}>
                  No designs yet
                </div>
              ) : (
                recentDesigns.map((d: any, i: number) => (
                  <div key={d.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '11px 16px',
                    borderBottom: i < recentDesigns.length - 1 ? '1px solid #1e1e1e' : 'none',
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#ddd', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</p>
                      <p style={{ fontSize: 11, color: '#555', margin: 0, marginTop: 1 }}>{d.serviceCode}</p>
                    </div>
                    <button
                      onClick={() => navigate(route(`/services/generator?designId=${d.id}`))}
                      style={{
                        flexShrink: 0, marginLeft: 8, padding: '4px 10px', borderRadius: 6,
                        background: 'rgba(255,217,61,0.08)', border: '1px solid rgba(255,217,61,0.2)',
                        color: GOLD, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Open
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Recent Downloads */}
            <div style={{ background: '#141414', border: '1px solid #272727', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', borderBottom: '1px solid #272727',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ccc', fontSize: 13, fontWeight: 600 }}>
                  <Download size={15} style={{ color: GOLD }} /> Downloads
                </div>
              </div>
              {recentDownloads.length === 0 ? (
                <div style={{ padding: '28px 16px', textAlign: 'center', color: '#555', fontSize: 13 }}>
                  No downloads yet
                </div>
              ) : (
                recentDownloads.map((d: any, i: number) => (
                  <div key={d.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '11px 16px',
                    borderBottom: i < recentDownloads.length - 1 ? '1px solid #1e1e1e' : 'none',
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#ddd', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.designName || d.design?.name || 'Plot'}
                      </p>
                      <p style={{ fontSize: 11, color: '#f87171', margin: 0, marginTop: 1 }}>-₹{d.creditsCharged}</p>
                    </div>
                    <span style={{ flexShrink: 0, marginLeft: 8, fontSize: 11, color: '#555' }}>
                      {new Date(d.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          </div>{/* End right column */}
          </div>{/* End main layout */}

        </div>
      </PageLayout.Content>
    </PageLayout>
  );
};

export default Dashboard;
